/**
 * IGRA-20 scaffold — static fixtures for the `/vpn/*` endpoints, shaped 1:1
 * from `src/sites/IgraNow/docs/sprints/sprint-01-vpn-mvp/API-CONTRACT.md`.
 *
 * NOT wired into httpClient yet: no screen in this package calls `/vpn/*`
 * today (the dashboard/devices/friends/gifts/payments/settings tabs are all
 * static IGRA-01 stub cards — see `src/modules/vpn/pages/*`). Auth
 * (`/profile/*`, `/security/*`) already runs against the real dev backend
 * for this site (site_id seeded by IGRA-00), so IGRA-20 did not need a mock
 * transport to verify registration/login/OTP end-to-end.
 *
 * IGRA-21+ (subscription/devices/friends/gifts/payments screens) is the
 * expected consumer: read a fixture below, gate it behind
 * `import.meta.env.VITE_IGRA_USE_MOCKS === '1'` in the relevant `*Api.ts`
 * (same pattern as `httpClient.ts`'s `getApiBaseUrl` — env override first,
 * real network otherwise), and expand this file with POST fixtures
 * (activate-trial, devices/purchase, gifts/buy, billing/checkout, …) as
 * those screens are built. Keep it data-only — no request interception
 * framework until a second consumer actually needs one.
 */

import type {
  VpnPoolAcceptResponse,
  VpnPoolInviteResponse,
  VpnPoolResponse,
  VpnPublicPoolInviteResponse,
} from '@/modules/vpn/types/pool'

export const isMocksEnabled = (): boolean => import.meta.env.VITE_IGRA_USE_MOCKS === '1'

export const vpnOverviewFixture = {
  access: {
    kind: 'trial',
    status: 'active',
    paid_until: '2026-09-10T00:00:00+03:00',
    days_left: 31,
    devices_used: 1,
    device_limit: 3,
    tg_linked: false,
    tg_bonus_available: true,
    // IGRA-25 — auto-renew flag for the "Платежи" screen block. NOT in the
    // API-CONTRACT.md overview example and not (yet) returned by the real
    // backend (`VpnController::accessBlock`) — typed as optional in
    // `types/vpn.ts`, the UI renders the block only when the field is
    // present. Documented as a contract gap in PROGRESS.md (IGRA-25).
    auto_renew: true,
  },
  key: {
    url: 'https://key.igra.click/s/Ab12Cd34Ef56',
    page_url: '/app/vpn/connect/Ab12Cd34Ef56',
    status: 'active',
  },
  pricing: {
    currency: 'RUB',
    // HK-IGRA.55 — мок-юзер действующий участник круга, а не приглашённый.
    invited: false,
    next_charge_amount: 279,
    next_charge_date: '2026-09-10',
    periods: [
      { period: 'month', price: 350, personal_price: 279 },
      { period: 'quarter', price: 990, personal_price: null },
      { period: 'year', price: 1990, personal_price: null },
    ],
  },
  // IGRA-41: next_price_if_joined — следующая ступень (круг из 3 → grid[4] = 255).
  circle: { id: 5, members: 3, seats: 10, is_founder: true, next_month_price: 279, next_price_if_joined: 255 },
}

/**
 * IGRA-21 — additional overview variants + activate-trial fixture, so the
 * Subscription screen's states (no key / expired / trial-just-activated) can
 * be exercised in the browser without a live backend. Selected via
 * `localStorage['igra_vpn_mock_state']` ('active' | 'no_key' | 'expired')
 * and the independent `localStorage['igra_vpn_mock_activate_fail']` flag in
 * `src/modules/vpn/api/vpnApi.ts` — see that file for how they map to a
 * fixture / simulated error.
 */
export const vpnOverviewNoKeyFixture = {
  ...vpnOverviewFixture,
  // IGRA-30: живой бэкенд отдаёт access: null, пока доступ не активирован —
  // фикстура повторяет реальную форму (стаб-объект ронял дашборд вживую)
  access: null,
  key: null,
  circle: null,
}

export const vpnOverviewExpiredFixture = {
  ...vpnOverviewFixture,
  access: {
    ...vpnOverviewFixture.access,
    status: 'inactive' as const,
    days_left: 0,
  },
  key: { ...vpnOverviewFixture.key, status: 'inactive' as const },
}

export const vpnActivateTrialFixture = {
  key: vpnOverviewFixture.key,
  access: {
    ...vpnOverviewFixture.access,
    kind: 'trial' as const,
    status: 'active' as const,
    devices_used: 0,
    device_limit: 2,
  },
}

/** Error code from API-CONTRACT.md "Ошибки" catalogue — thrown by the mock
 * `activate-trial` call when `igra_vpn_mock_state === 'activate_error'`. */
export const VPN_UPSTREAM_UNAVAILABLE = 'VPN_UPSTREAM_UNAVAILABLE'

/**
 * HK-IGRA.49 → Sprint 16 — предложение докупки приходит готовым из
 * `GET /vpn/devices` (`VpnBillingService::deviceOffer`), фронт его не считает.
 * Мок повторяет пример из API-CONTRACT.md: годовой подписчик, до конца года
 * ровно полгода — месячная цена и списание сейчас различаются.
 */
export const vpnDevicePurchaseOfferFixture = {
  extra: 1,
  monthly_price: 50,
  charge_now: 145,
  period: 'year' as const,
  price_per_device: 290,
  price_per_device_month: 50,
  options: [
    { extra: 1, monthly_price: 50, charge_now: 145 },
    { extra: 3, monthly_price: 150, charge_now: 435 },
    { extra: 5, monthly_price: 250, charge_now: 725 },
    { extra: 10, monthly_price: 500, charge_now: 1450 },
  ],
}

/**
 * Sprint 16 — ответы `GET /vpn/devices/purchase-offer?extra=N` для своего
 * числа. Мок суммы не считает: знает только варианты кнопок и «+7» из
 * контракта, на остальные N отвечает ошибкой (см. `devicesApi.ts`).
 */
export const vpnDevicePurchaseQuotesFixture = [
  ...vpnDevicePurchaseOfferFixture.options,
  { extra: 7, monthly_price: 350, charge_now: 1015 },
]

export const vpnDevicesFixture = {
  device_limit: 3,
  devices: [
    {
      hwid: '3F2A9C1B',
      device_model: 'iPhone15,2',
      device_os: 'iOS',
      first_seen: '2026-07-20T10:00:00+03:00',
      last_seen: '2026-08-10T09:30:00+03:00',
    },
  ],
  purchase_offer: vpnDevicePurchaseOfferFixture,
}

// IGRA-22 — "no devices yet" shape (empty-state acceptance case).
// HK-IGRA.49 — месячный подписчик: списание равно месячной цене, поэтому в
// интерфейсе вторая строка не появляется.
export const vpnDevicesEmptyFixture = {
  device_limit: 3,
  devices: [],
  purchase_offer: {
    extra: 1,
    monthly_price: 50,
    charge_now: 50,
    period: 'month' as const,
    price_per_device: 50,
    price_per_device_month: 50,
    options: [
      { extra: 1, monthly_price: 50, charge_now: 50 },
      { extra: 3, monthly_price: 150, charge_now: 150 },
      { extra: 5, monthly_price: 250, charge_now: 250 },
      { extra: 10, monthly_price: 500, charge_now: 500 },
    ],
  },
}

// IGRA-473 — много устройств: восемь одинаковых «Pixel 8 · Android».
// Sprint 16 — потолка больше нет, докупка доступна и здесь.
export const vpnDevicesManyFixture = {
  device_limit: 8,
  devices: Array.from({ length: 8 }, (_, i) => ({
    hwid: `MAXHWID${i}`,
    device_model: i === 0 ? 'iPhone15,2' : 'Pixel 8',
    device_os: i === 0 ? 'iOS' : 'Android',
    first_seen: '2026-07-20T10:00:00+03:00',
    last_seen: '2026-08-10T09:30:00+03:00',
    // IGRA-473 — ради чего задача и заведена: восемь одинаковых «Pixel 8 ·
    // Android» различимы только по своим названиям. Подписано одно, чтобы в
    // моках были видны оба вида строки — со своим именем и без него.
    custom_name: i === 1 ? 'мамин телефон' : null,
  })),
  purchase_offer: vpnDevicePurchaseOfferFixture,
}

/** Error code from API-CONTRACT.md "Ошибки" catalogue — thrown by the mock
 * `devices/purchase` / `devices/purchase-offer` for a bad `extra`. */
export const VPN_DEVICES_PURCHASE_INVALID = 'VPN_DEVICES_PURCHASE_INVALID'

export const vpnReferralFixture = {
  link: 'https://igra.now/r/AbCdEf',
  invited_count: 3,
  days_earned: 10,
  connect_bonus_left: 3,
  invited: [
    { name: 'Иван Петров', status: 'paid', joined_at: '2026-07-15T00:00:00+03:00' },
    { name: 'Мария Сидорова', status: 'paid', joined_at: '2026-08-01T00:00:00+03:00' },
    { name: 'Друг 3', status: 'connected', joined_at: '2026-08-05T00:00:00+03:00' },
  ],
}

// IGRA-23 — invited_count === 0 shape, for the "0 приглашённых → empty-state"
// acceptance case (task-level fixture, not env-gated by default).
export const vpnReferralEmptyFixture = {
  link: 'https://igra.now/r/AbCdEf',
  invited_count: 0,
  days_earned: 0,
  connect_bonus_left: 5,
  invited: [],
}

// Full price grid — front-loaded ladder, PRD §6.4 variant A (IGRA-42,
// operator decision 2026-08-13): 1..10 members → 350..199 ₽. Must stay in sync
// with the backend seed (`modules/vpn/config/price-grid.php`).
export const vpnCirclePriceGrid = [
  { members: 1, price: 350 },
  { members: 2, price: 309 },
  { members: 3, price: 279 },
  { members: 4, price: 255 },
  { members: 5, price: 239 },
  { members: 6, price: 225 },
  { members: 7, price: 215 },
  { members: 8, price: 209 },
  { members: 9, price: 204 },
  { members: 10, price: 199 },
]

export const vpnCircleFixture = {
  circle: {
    id: 5,
    is_founder: true,
    seats: 10,
    members: [
      { name: 'Ты', joined_at: '2026-06-01T00:00:00+03:00', is_you: true },
      { name: 'Иван Петров', joined_at: '2026-07-15T00:00:00+03:00', is_you: false },
      { name: 'Мария Сидорова', joined_at: '2026-08-01T00:00:00+03:00', is_you: false },
    ],
    current_price: 279,
    next_price_if_joined: 255,
    grid: vpnCirclePriceGrid,
  },
}

// IGRA-23 — "not a member" shape (empty-state + mechanics explainer acceptance case).
// IGRA-44 E.1 — сетка и счётчики верхнего уровня: пустое состояние тоже
// называет цену места (API-CONTRACT.md, «Пустое состояние»). Участников здесь
// нет и быть не может (правило 5 IGRA-41).
export const vpnCircleNotMemberFixture = {
  circle: null,
  seats: 10,
  members_count: 0,
  grid: vpnCirclePriceGrid,
}

// IGRA-23 — full circle (10/10, no next tier) edge case.
export const vpnCircleFullFixture = {
  circle: {
    id: 9,
    is_founder: false,
    seats: 10,
    members: Array.from({ length: 10 }, (_, i) => ({
      name: i === 4 ? 'Ты' : `Друг ${i + 1}`,
      joined_at: '2026-07-01T00:00:00+03:00',
      is_you: i === 4,
    })),
    current_price: 199,
    next_price_if_joined: null,
    grid: vpnCirclePriceGrid,
  },
}

export const vpnGiftsFixture = {
  options: [
    { period: 'month', price: 350 },
    { period: 'quarter', price: 990 },
    { period: 'year', price: 1990 },
  ],
  my_gifts: [],
}

// IGRA-24 — populated `my_gifts` demo fixture: one gift in each status, so
// every "Мои подарки" row state (не оплачен / оплачен / активирован) is
// visible without needing to actually buy anything first.
export const vpnGiftsPopulatedFixture = {
  options: vpnGiftsFixture.options,
  my_gifts: [
    { code: 'GIFT-PEND1', period: 'month', status: 'pending', created_at: '2026-08-05T10:00:00+03:00' },
    { code: 'GIFT-PAID1', period: 'quarter', status: 'paid', created_at: '2026-08-01T10:00:00+03:00' },
    { code: 'GIFT-DONE1', period: 'year', status: 'activated', created_at: '2026-07-01T10:00:00+03:00' },
  ],
}

export const vpnBillingPaymentsFixture: Array<{
  id: number
  description: string
  total: number
  currency: string
  status: 'pending' | 'success' | 'fail'
  create_datetime: string
  payment_url: string | null
  can_retry: boolean
}> = []

/**
 * IGRA-25 — populated payments history: one row per status, so every
 * "Платежи" list state (в обработке / оплачен / не прошёл) is visible at a
 * glance (same idea as `vpnGiftsPopulatedFixture`). The pending row carries a
 * `payment_url` (API-CONTRACT.md: url is null only once paid) so the
 * "Оплатить" affordance + checkout polling can be exercised on mocks. The
 * empty scaffold fixture above stays as the "Пусто" acceptance state
 * (`localStorage['igra_vpn_billing_mock_state']='empty'` — see `billingApi.ts`).
 */
export const vpnBillingPaymentsPopulatedFixture: typeof vpnBillingPaymentsFixture = [
  {
    id: 910003,
    description: 'Подписка VPN — месяц',
    total: 279,
    currency: 'RUB',
    status: 'pending',
    create_datetime: '2026-08-10 12:00:00',
    payment_url: 'https://pay.example/mock-invoice-910003',
    can_retry: true,
  },
  {
    id: 910002,
    description: 'Подписка VPN — 3 месяца',
    total: 990,
    currency: 'RUB',
    status: 'fail',
    create_datetime: '2026-08-01 18:30:00',
    payment_url: 'https://pay.example/mock-invoice-910002',
    can_retry: true,
  },
  {
    id: 910001,
    description: 'Подписка VPN — месяц',
    total: 350,
    currency: 'RUB',
    status: 'success',
    create_datetime: '2026-07-10 09:15:00',
    payment_url: null,
    can_retry: false,
  },
]

/**
 * HK-IGRA.153 — «Способы оплаты». Две карты, чтобы в моках было видно
 * главное: признак «этой спишут продление» стоит НЕ на первой в списке (его
 * считает сервер), и просроченная карта из списка не исчезает, а помечается.
 */
export const vpnPaymentMethodsFixture = [
  {
    id: 77001,
    provider: 'cloudpayments' as const,
    brand: 'Visa',
    last4: '4242',
    exp: '12/34',
    expired: false,
    is_renewal: false,
  },
  {
    id: 77002,
    provider: 'cloudpayments' as const,
    brand: 'MasterCard',
    last4: '0042',
    exp: '08/29',
    expired: false,
    is_renewal: false,
  },
  // HK-IGRA.164 — смешанный список: у ЮKassa свой автоинкремент, и число 77002
  // здесь повторяется намеренно. Так в dev видно, что строки не путаются.
  {
    id: 77002,
    provider: 'yookassa' as const,
    brand: 'MIR',
    last4: '7777',
    exp: '11/30',
    expired: false,
    is_renewal: true,
  },
]

export const vpnPaymentMethodsEmptyFixture: typeof vpnPaymentMethodsFixture = []

/**
 * IGRA-110 — `GET /vpn/pool`. Базовая фикстура снята с примера §8 ТЗ
 * «Поделиться подпиской» (владелец, один участник, одно ожидающее
 * приглашение); добавлено только `host_key_url` — его отдаёт контроллер, а в
 * примере §8 его нет. У владельца оно `null`: продлевать «за всех» по чужому
 * ключу ему незачем (R17).
 *
 * ФОРМАТ ДАТ — БОЕВОЙ, а не ISO. Все даты бэкенд отдаёт строкой MySQL
 * `Y-m-d H:i:s` (это столбцы `vpn_access`/`vpn_pool_invite`, отданные как
 * есть), и только `personal_offer.until` — голой датой `Y-m-d`. Пока фикстуры
 * отдавали ISO с `T…Z`, ветка нормализации в `formatDateLong` не проверялась
 * ни одним тестом пула, а день в ассертах «плавал» по часовому поясу — под это
 * даже ослабили проверку. Сверено с `PoolControllerTest` и
 * `PoolMemberDevicePurchaseTest`.
 *
 * Тип проставлен нарочно: расхождение полей с §8 обязан ловить компилятор, а
 * не человек на ревью.
 */
export const vpnPoolFixture: VpnPoolResponse = {
  role: 'owner',
  enabled: true,
  host: { name: 'Алексей', user_id: 123 },
  paid_until: '2026-10-12 00:00:00',
  devices: { limit: 5, used: 3, free: 2 },
  seats: { max: 4, used: 1, pending: 1, free: 2 },
  members: [
    {
      user_id: 123,
      name: 'Алексей',
      is_owner: true,
      is_you: true,
      devices_in_pool: 2,
      joined_at: null,
    },
    {
      user_id: 456,
      name: 'Маша',
      is_owner: false,
      is_you: false,
      devices_in_pool: 1,
      joined_at: '2026-08-28 10:00:00',
    },
  ],
  invites: [
    {
      id: 7,
      email: 'dima@example.com',
      channel: 'email',
      expires_at: '2026-09-08 12:00:00',
      email_sent: true,
      link: 'https://igra.now/invite/8f3c9d1b4a7e2f60c5d8b3a1e9f47c26',
    },
  ],
  can_invite: true,
  invite_blocked_reason: null,
  emails_left_today: 3,
  // Владельцу бэкенд шлёт `me` БЕЗ личных устройств: `devices_extra` в его
  // строке это докупленные слоты всего пула (R15), а не личные устройства
  // участника (R12) — `PoolOverviewBuilder::meBlock` на `$me === null` отдаёт
  // ровно эти три значения. Выхода у владельца тоже нет: `leave_offer` — null.
  me: {
    devices_in_pool: 2,
    devices_personal: 0,
    devices_personal_until: null,
    personal_offer: null,
  },
  leave_offer: null,
  host_key_url: null,
}

/**
 * IGRA-110 экран 07 — тот же пул ГЛАЗАМИ УЧАСТНИКА. Состав тот же, что у
 * владельца, но роль другая, приглашать участник не может (R2/R5), а `me`
 * рассказывает про ЕГО устройства: одно гарантированное в общей подписке
 * (R11) и счёт за личное (R16).
 *
 * `host_key_url` — публичная страница ключа ВЛАДЕЛЬЦА: по ней участник
 * продлевает подписку за всех (R17). У владельца она `null`.
 *
 * `invite_blocked_reason` у участника — ВСЕГДА `null`: причину отказа
 * приглашать бэкенд считает только для владельца
 * (`PoolOverviewBuilder::poolView`, `$viewerIsMember ? null : $blocked`).
 * Прежняя `'seats_full'` была выдумкой мока, и именно она маскировала дыру:
 * с ней блок приглашения на владельческом экране выглядел закрытым, хотя в
 * проде участник видел там живую форму с кнопкой «Создать ссылку».
 */
export const vpnPoolMemberFixture: VpnPoolResponse = {
  ...vpnPoolFixture,
  role: 'member',
  members: [
    {
      user_id: 123,
      name: 'Алексей',
      is_owner: true,
      is_you: false,
      devices_in_pool: 2,
      joined_at: null,
    },
    {
      user_id: 456,
      name: 'Маша',
      is_owner: false,
      is_you: true,
      devices_in_pool: 1,
      joined_at: '2026-08-28 10:00:00',
    },
  ],
  invites: [],
  can_invite: false,
  invite_blocked_reason: null,
  emails_left_today: null,
  me: {
    devices_in_pool: 1,
    devices_personal: 2,
    devices_personal_until: '2026-09-30 23:59:59',
    personal_offer: { charge_now: 140, monthly: 150, until: '2026-09-30', days: 28 },
  },
  leave_offer: { circle_price: 309, regular_price: 350 },
  host_key_url: 'https://key.igra.click/s/HostKeyUid42',
}

/** Участник, у которого личных устройств ещё нет: докупка ему предлагается. */
export const vpnPoolMemberNoPersonalFixture: VpnPoolResponse = {
  ...vpnPoolMemberFixture,
  me: {
    ...vpnPoolMemberFixture.me!,
    devices_personal: 0,
    devices_personal_until: null,
  },
}

/** Мест больше нет (R3): поле e-mail на экране прячется, остаётся текст причины. */
export const vpnPoolSeatsFullFixture: VpnPoolResponse = {
  ...vpnPoolFixture,
  seats: { max: 4, used: 4, pending: 0, free: 0 },
  can_invite: false,
  invite_blocked_reason: 'seats_full',
}

/**
 * Пять писем за сутки уже ушли (R5a). Приглашение ссылкой в лимит не входит,
 * поэтому кнопки ссылки на экране остаются живыми.
 */
export const vpnPoolEmailLimitFixture: VpnPoolResponse = {
  ...vpnPoolFixture,
  can_invite: true,
  invite_blocked_reason: 'email_limit',
  emails_left_today: 0,
}

/** Пробный доступ (R1): пула нет, делиться нечем — §8 отдаёт голый ответ. */
export const vpnPoolTrialFixture: VpnPoolResponse = {
  role: 'none',
  enabled: true,
  host: null,
  paid_until: null,
  devices: null,
  seats: null,
  members: null,
  invites: null,
  can_invite: false,
  invite_blocked_reason: 'trial_only',
  emails_left_today: null,
  me: null,
  leave_offer: null,
  host_key_url: null,
}

/**
 * IGRA-110 §11 — флаг `vpn.pool.enabled` выключен на сайте. Пул при этом
 * ПРОДОЛЖАЕТ работать: состав, устройства и участники на месте, флаг гейтит
 * только создание новых приглашений. Поэтому у владельца `role: 'owner'` и
 * прежний состав, но звать больше некого (`can_invite: false`), а раздел
 * `/share` фронт прячет целиком.
 */
export const vpnPoolDisabledFixture: VpnPoolResponse = {
  ...vpnPoolFixture,
  enabled: false,
  can_invite: false,
}

/** Ответ `POST /vpn/pool/invite` — приглашение ссылкой (письма не было). */
export const vpnPoolInviteCreatedFixture: VpnPoolInviteResponse = {
  invite: {
    id: 8,
    link: 'https://igra.now/invite/1a2b3c4d5e6f708192a3b4c5d6e7f809',
    expires_at: '2026-09-10 12:00:00',
  },
  email_sent: false,
}

/** Ответ публичной `GET /api/vpn/public/pool/invite/{token}`. */
export const vpnPublicPoolInviteFixture: VpnPublicPoolInviteResponse = {
  status: 'valid',
  host_name: 'Алексей',
  paid_until: '2026-10-12 00:00:00',
  devices_free: 2,
  devices_limit: 5,
  email: 'dima@example.com',
  expires_at: '2026-09-08 12:00:00',
}

/**
 * Три нерабочих состояния токена (R6): срок вышел, владелец отозвал,
 * приглашение уже приняли. Экран у всех трёх один — «больше не действует».
 */
export const vpnPublicPoolInviteExpiredFixture: VpnPublicPoolInviteResponse = {
  ...vpnPublicPoolInviteFixture,
  status: 'expired',
}

export const vpnPublicPoolInviteRevokedFixture: VpnPublicPoolInviteResponse = {
  ...vpnPublicPoolInviteFixture,
  status: 'revoked',
}

export const vpnPublicPoolInviteAcceptedFixture: VpnPublicPoolInviteResponse = {
  ...vpnPublicPoolInviteFixture,
  status: 'accepted',
}

/** Приглашение ссылкой: адреса нет, войти можно на любой e-mail (R7). */
export const vpnPublicPoolInviteLinkFixture: VpnPublicPoolInviteResponse = {
  ...vpnPublicPoolInviteFixture,
  email: null,
}

/**
 * IGRA-110 §8 — `GET /vpn/overview` УЧАСТНИКА: аддитивные `pool` и `stars`.
 * Доступ у него живой, но чужой — даты и лимит принадлежат подписке владельца.
 * Баланс звёзд ноль с названной причиной (R23).
 */
export const vpnOverviewPoolMemberFixture = {
  ...vpnOverviewFixture,
  access: {
    ...vpnOverviewFixture.access,
    kind: 'paid' as const,
    status: 'active' as const,
    paid_until: '2026-10-12 00:00:00',
    days_left: 39,
    devices_used: 1,
    device_limit: 5,
    tg_linked: true,
    tg_bonus_available: false,
  },
  circle: null,
  pool: {
    role: 'member' as const,
    host_name: 'Алексей' as string | null,
    paid_until: '2026-10-12 00:00:00' as string | null,
    devices_free: 2 as number | null,
    // §11 — флаг сайта, продублированный в овервью: по нему вкладку раздела
    // прячут экраны, которые за `GET /vpn/pool` не ходят.
    enabled: true,
  },
}

/** US-12 — подписка владельца закончилась: доступ погас у всего пула (R18). */
export const vpnOverviewPoolMemberExpiredFixture = {
  ...vpnOverviewPoolMemberFixture,
  access: {
    ...vpnOverviewPoolMemberFixture.access,
    status: 'inactive' as const,
    days_left: 0,
  },
  key: { ...vpnOverviewFixture.key, status: 'inactive' as const },
}

/**
 * IGRA-110 §8 — `GET /vpn/devices` участника: свои устройства, блок `pool`
 * (гарантированное + личные) и `personal_offer` ВМЕСТО `purchase_offer`.
 */
export const vpnDevicesPoolMemberFixture = {
  device_limit: 3,
  devices: [
    {
      hwid: 'MEMBER01',
      device_model: 'Pixel 8',
      device_os: 'Android',
      first_seen: '2026-08-28T10:00:00+03:00',
      last_seen: '2026-09-02T21:10:00+03:00',
    },
  ],
  purchase_offer: null,
  // `personal_until` — ГОЛАЯ ДАТА: `VpnController` режет её `substr(…, 0, 10)`,
  // потому что экран пишет «до 30 сентября», а не «до 30 сентября 23:59:59».
  pool: { guaranteed: 1, personal: 2, personal_until: '2026-09-30' as string | null },
  personal_offer: { charge_now: 140, monthly: 150, until: '2026-09-30', days: 28 },
}

/** Ответ `POST /vpn/pool/accept` — участник в пуле, ключ у него свой. */
export const vpnPoolAcceptFixture: VpnPoolAcceptResponse = {
  success: true,
  key_url: 'https://key.igra.click/s/Ab12Cd34Ef56',
  paid_until: '2026-10-12 00:00:00',
}
